/**
 * Endorsement Service
 *
 * This service handles storing and retrieving endorsement data in DynamoDB
 */

import { generateClient } from 'aws-amplify/data';
import { type Schema } from '../../amplify/data/resource';

// Initialize client lazily to avoid Amplify configuration warnings
let client: ReturnType<typeof generateClient<Schema>> | null = null;

const getClient = () => {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
};

// Type for raw endorsement data from DynamoDB
type RawEndorsementData = {
  applicationId: string;
  judgeEmail: string;
  judgeName: string;
  mark: number;
  comment?: string | null;
  submittedAt: string;
  updatedAt?: string | null;
};

export interface Endorsement {
  applicationId: string;
  judgeEmail: string;
  judgeName: string;
  mark: number;
  comment?: string;
  submittedAt: string;
  updatedAt?: string;
}

class EndorsementService {
  /**
   * Create a new endorsement
   */
  async createEndorsement(
    applicationId: string,
    judgeEmail: string,
    judgeName: string,
    mark: number,
    comment?: string
  ): Promise<void> {
    // console.log(`Creating endorsement for application ${applicationId} by judge ${judgeEmail}`);

    const endorsement = {
      applicationId,
      judgeEmail,
      judgeName,
      mark,
      comment: comment || '',
      submittedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await getClient().models.Endorsement.create(endorsement);
    // console.log('Endorsement created successfully in DynamoDB');
  }

  /**
   * Update an existing endorsement
   */
  async updateEndorsement(
    applicationId: string,
    judgeEmail: string,
    mark: number,
    comment?: string
  ): Promise<void> {
    // console.log(`Updating endorsement for application ${applicationId} by judge ${judgeEmail}`);

    await getClient().models.Endorsement.update({
      applicationId,
      judgeEmail,
      mark,
      comment: comment || '',
      updatedAt: new Date().toISOString()
    });

    // console.log('Endorsement updated successfully in DynamoDB');
  }

  /**
   * Get endorsement for a specific application and judge
   */
  async getEndorsement(applicationId: string, judgeEmail: string): Promise<Endorsement | null> {
    // console.log(`Getting endorsement for application ${applicationId} by judge ${judgeEmail}`);

    const result = await getClient().models.Endorsement.get({
      applicationId,
      judgeEmail
    });

    if (result.data) {
      // console.log('Found endorsement:', result.data);
      return {
        applicationId: result.data.applicationId,
        judgeEmail: result.data.judgeEmail,
        judgeName: result.data.judgeName,
        mark: result.data.mark,
        comment: result.data.comment || undefined,
        submittedAt: result.data.submittedAt,
        updatedAt: result.data.updatedAt || undefined
      };
    }

    // console.log('No endorsement found');
    return null;
  }

  /**
   * Get all endorsements for a specific application
   */
  async getEndorsementsForApplication(applicationId: string): Promise<Endorsement[]> {
    // console.log(`Getting all endorsements for application ${applicationId}`);

    const allEndorsements: RawEndorsementData[] = [];
    let nextToken: string | null | undefined;

    do {
      const result = await getClient().models.Endorsement.list({
        filter: {
          applicationId: {
            eq: applicationId
          }
        },
        nextToken
      });

      if (result.data) {
        allEndorsements.push(...result.data);
      }

      nextToken = result.nextToken;
    } while (nextToken);

    const endorsements = allEndorsements.map(item => ({
      applicationId: item.applicationId,
      judgeEmail: item.judgeEmail,
      judgeName: item.judgeName,
      mark: item.mark,
      comment: item.comment || undefined,
      submittedAt: item.submittedAt,
      updatedAt: item.updatedAt || undefined
    }));

    // console.log(`Found ${endorsements.length} endorsements for application ${applicationId}`);
    return endorsements;
  }

  /**
   * Get all endorsements by a specific judge
   */
  async getEndorsementsByJudge(judgeEmail: string): Promise<Endorsement[]> {
    // console.log(`Getting all endorsements by judge ${judgeEmail}`);

    const allEndorsements: RawEndorsementData[] = [];
    let nextToken: string | null | undefined;

    do {
      const result = await getClient().models.Endorsement.list({
        filter: {
          judgeEmail: {
            eq: judgeEmail
          }
        },
        nextToken
      });

      if (result.data) {
        allEndorsements.push(...result.data);
      }

      nextToken = result.nextToken;
    } while (nextToken);

    const endorsements = allEndorsements.map(item => ({
      applicationId: item.applicationId,
      judgeEmail: item.judgeEmail,
      judgeName: item.judgeName,
      mark: item.mark,
      comment: item.comment || undefined,
      submittedAt: item.submittedAt,
      updatedAt: item.updatedAt || undefined
    }));

    console.log(`Found ${endorsements.length} endorsements by judge ${judgeEmail}`);
    return endorsements;
  }

  /**
   * Delete an endorsement
   */
  async deleteEndorsement(applicationId: string, judgeEmail: string): Promise<void> {
    // console.log(`Deleting endorsement for application ${applicationId} by judge ${judgeEmail}`);

    await getClient().models.Endorsement.delete({
      applicationId,
      judgeEmail
    });

    // console.log('Endorsement deleted successfully from DynamoDB');
  }

  /**
   * Check if a judge has already endorsed an application
   */
  async hasJudgeEndorsed(applicationId: string, judgeEmail: string): Promise<boolean> {
    const endorsement = await this.getEndorsement(applicationId, judgeEmail);
    return endorsement !== null;
  }
}

export const endorsementService = new EndorsementService();
